import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import type { Me } from '../lib/session.js';
import { dateTime, num } from '../lib/format.js';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Loading,
  PageHeader,
  Panel,
  Tile,
} from '../components/ui.js';
import { Badge } from '../components/2b-status.js';
import { SendTarget } from '../components/2b-sendtarget.js';
import '../screens-2b.css';

interface SimState {
  available: boolean;
  clockMode: string;
  now: string;
  timezone: string;
  counts: {
    races: number;
    contacts: number;
    orders: number;
    active_trips: number;
    messages: number;
    open_candidates: number;
  };
}

/** Posun casu bezi na pozadi; administrace se diva na prubeh (SPEC kap. 11). */
interface SimJob {
  id: string;
  status: 'running' | 'done' | 'failed';
  from: string;
  target: string;
  now: string;
  progress: number;
  steps: number;
  sent: number;
  held: number;
  deferred: number;
  suppressed: number;
  merged: number;
  expired: number;
  candidatesCreated: number;
  error?: string;
}

export function Simulator({ me }: { me: Me }): JSX.Element {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [raceId, setRaceId] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [done, setDone] = useState<string | null>(null);
  /* Akce, které se musí potvrdit: posun času (nejde vrátit), zrušení závodu, reset dat. */
  const [ask, setAsk] = useState<
    | { kind: 'advance'; body: Record<string, unknown>; text: string }
    | { kind: 'cancel' }
    | { kind: 'reset' }
    | null
  >(null);

  const state = useQuery<SimState>({
    queryKey: ['simulator'],
    queryFn: () => api.get('/api/simulator'),
  });
  const races = useQuery<{ items: Array<{ raceId: string; name: string }> }>({
    queryKey: ['races', 'sim'],
    queryFn: () => api.get('/api/races?pageSize=200'),
  });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['simulator'] });
    void client.invalidateQueries({ queryKey: ['me'] });
    void client.invalidateQueries({ queryKey: ['overview'] });
  };

  const advance = useMutation<SimJob, Error, Record<string, unknown>>({
    mutationFn: (body) => api.post('/api/simulator/advance', body),
    onSuccess: (job) => {
      setError(null);
      setJobId(job.id);
    },
    onError: (err) => setError(err.message),
  });

  const job = useQuery<SimJob>({
    queryKey: ['simulator-job', jobId],
    queryFn: () => api.get<SimJob>(`/api/simulator/advance/${jobId as string}`),
    enabled: jobId !== null,
    refetchInterval: (query) =>
      query.state.data && query.state.data.status === 'running' ? 1000 : false,
  });

  const running = job.data?.status === 'running';
  const jobStatus = job.data?.status;
  const jobNow = job.data?.now;
  useEffect(() => {
    // Po dokonceni posunu prekreslime cas v pruhu a dlazdice.
    if (jobStatus && jobStatus !== 'running') refresh();
  }, [jobStatus, jobNow, refresh]);

  const action = useMutation<unknown, Error, Record<string, unknown>>({
    mutationFn: (body) => api.post('/api/simulator/action', body),
    onSuccess: () => {
      setError(null);
      setDone('Akce proběhla. Čísla níže se po chvíli přepočítají.');
      refresh();
    },
    onError: (err) => setError(err.message),
  });

  const seed = useMutation<unknown, Error, { reset: boolean; scale: string }>({
    mutationFn: (body) => api.post('/api/simulator/seed', body),
    onSuccess: () => {
      setError(null);
      setDone('Demo data jsou hotová.');
      refresh();
    },
    onError: (err) => setError(err.message),
  });

  if (state.isLoading) return <Loading what="simulátor" />;
  if (state.isError)
    return (
      <ErrorState
        title="Simulátor se nepodařilo načíst"
        error={state.error}
        onRetry={() => void state.refetch()}
      />
    );
  const data = state.data;
  if (!data) return <Loading />;

  if (!data.available) {
    return (
      <>
        <PageHeader
          title="Simulátor"
          description="Zkouška celé cesty zákazníka na simulovaných hodinách."
        />
        <EmptyState title="Simulátor je tady vypnutý" icon="clock">
          Simulátor potřebuje simulované hodiny (nastavení CLOCK_MODE=simulated) a nesmí běžet v
          produkci. Teď hub běží se{' '}
          {data.clockMode === 'system' ? 'skutečnými hodinami' : 'simulovanými hodinami'}.
        </EmptyState>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Simulátor"
        description="Posouvá čas hubu dopředu, aby šlo ověřit celou cestu zákazníka, aniž by se čekalo měsíc. Čas posunout dopředu jde, zpět ne."
      />
      <SendTarget
        me={me}
        what="Posun času a akce níže pouštějí plánovač a rozhodování o zprávách."
      />
      <div className="help">
        <p>
          Simulátor posouvá <b>čas hubu</b>, ne čas serveru. V ostrém provozu se nepoužívá: běží tam
          skutečný čas a tahle obrazovka se nezobrazuje.
        </p>
      </div>

      {error ? <ErrorState title="Akce se nepovedla" error={new Error(error)} /> : null}
      {done ? (
        <p className="note" role="status">
          {done}
        </p>
      ) : null}

      <Panel title="Simulované datum">
        <div className="row">
          <Badge tone="sim">{dateTime(data.now, data.timezone)}</Badge>
          <span className="note">pásmo {data.timezone}</span>
        </div>
        <div className="row s2b-gap" data-testid="sim-controls">
          {(
            [
              ['+ hodina', { hours: 1 }, '1 hodinu'],
              ['+ den', { days: 1 }, '1 den'],
              ['+ týden', { weeks: 1 }, '1 týden'],
            ] as const
          ).map(([text, body, span]) => (
            <button
              key={text}
              type="button"
              className="btn"
              disabled={running}
              onClick={() => setAsk({ kind: 'advance', body, text: span })}
            >
              {text}
            </button>
          ))}
          <button
            type="button"
            className="btn pri"
            onClick={() => setAsk({ kind: 'advance', body: { days: 30 }, text: '30 dní' })}
            disabled={running}
            data-testid="advance-month"
          >
            + měsíc
          </button>
          <input
            type="date"
            value={targetDate}
            aria-label="Posunout na datum"
            onChange={(e) => setTargetDate(e.target.value)}
          />
          <button
            type="button"
            className="btn"
            disabled={!targetDate || running}
            onClick={() =>
              setAsk({
                kind: 'advance',
                body: { to: new Date(`${targetDate}T08:00:00Z`).toISOString() },
                text: `na ${targetDate}`,
              })
            }
          >
            Posunout na datum
          </button>
        </div>

        {job.data ? (
          <div
            className="panel s2b-gap"
            data-testid={running ? 'advance-progress' : 'advance-result'}
          >
            <h3>{running ? 'Posouvám čas…' : 'Výsledek posunu'}</h3>
            <p className="note">
              {dateTime(job.data.from, data.timezone)} → {dateTime(job.data.now, data.timezone)}
              {running ? ` (cíl ${dateTime(job.data.target, data.timezone)})` : ''} ·{' '}
              {num(job.data.steps)} běhů engine
            </p>
            {running ? (
              <progress
                className="s2b-progress"
                max={100}
                value={job.data.progress}
                aria-label="Průběh posunu"
              />
            ) : null}
            {job.data.status === 'failed' ? (
              <ErrorState
                title="Posun času selhal"
                error={new Error(job.data.error ?? 'Posun selhal.')}
              />
            ) : null}
            <div className="tiles">
              <Tile label="Odesláno" value={num(job.data.sent)} />
              <Tile label="Sloučeno" value={num(job.data.merged)} />
              <Tile label="Drženo" value={num(job.data.held)} />
              <Tile label="Odloženo" value={num(job.data.deferred)} />
              <Tile label="Potlačeno" value={num(job.data.suppressed)} />
              <Tile label="Propadlo" value={num(job.data.expired)} />
            </div>
          </div>
        ) : null}
      </Panel>

      <div className="tiles">
        <Tile label="Závody" value={num(data.counts.races)} />
        <Tile label="Kontakty" value={num(data.counts.contacts)} />
        <Tile label="Objednávky" value={num(data.counts.orders)} />
        <Tile label="Aktivní cesty" value={num(data.counts.active_trips)} />
        <Tile label="Zprávy" value={num(data.counts.messages)} />
        <Tile label="Kandidáti na řadě" value={num(data.counts.open_candidates)} />
      </div>

      <Panel title="Akce k závodu">
        <div className="row">
          <select value={raceId} onChange={(e) => setRaceId(e.target.value)} aria-label="Závod">
            <option value="">Vyberte závod</option>
            {races.data?.items.map((r) => (
              <option key={r.raceId} value={r.raceId}>
                {r.name} ({r.raceId})
              </option>
            ))}
          </select>
        </div>
        {raceId ? (
          <div className="row s2b-gap">
            <button
              className="btn"
              onClick={() =>
                action.mutate({
                  action: 'race_date',
                  raceId,
                  dateStart: new Date(new Date(data.now).getTime() + 60 * 86_400_000).toISOString(),
                })
              }
            >
              Změnit termín (za 60 dní)
            </button>
            <button type="button" className="btn dng" onClick={() => setAsk({ kind: 'cancel' })}>
              Zrušit závod
            </button>
            <button
              className="btn"
              onClick={() =>
                action.mutate({
                  action: 'promise',
                  raceId,
                  promiseBy: new Date(new Date(data.now).getTime() + 20 * 86_400_000).toISOString(),
                })
              }
            >
              Nastavit slib doručení
            </button>
            <button
              className="btn"
              onClick={() => action.mutate({ action: 'tickets_delivered', raceId })}
            >
              Vstupenky doručeny
            </button>
            <button className="btn" onClick={() => action.mutate({ action: 'race_pack', raceId })}>
              Race pack připraven
            </button>
            <button
              className="btn"
              onClick={() => action.mutate({ action: 'approve_review', raceId })}
            >
              Schválit hodnocení
            </button>
            <button
              className="btn"
              onClick={() =>
                action.mutate({
                  action: 'availability',
                  raceId,
                  category: 'GA',
                  availability: 'in_stock',
                  price: 199,
                })
              }
            >
              Shoda watchdogu (GA dostupné)
            </button>
          </div>
        ) : null}
      </Panel>

      <Panel title="Demo data">
        <p className="note">
          Generátor vytváří vždy stejná smyšlená data, žádní skuteční lidé v nich nejsou. Reset
          smaže všechna data hubu.
        </p>
        <div className="row">
          <button className="btn" onClick={() => seed.mutate({ reset: false, scale: 'small' })}>
            Doplnit malá demo data
          </button>
          <button type="button" className="btn dng" onClick={() => setAsk({ kind: 'reset' })}>
            Reset a plná demo data
          </button>
        </div>
        {seed.isPending ? <p className="note">Generuji demo data…</p> : null}
      </Panel>

      <p className="note">V produkci se simulátor nezobrazuje a hodiny musí být systémové.</p>

      <ConfirmDialog
        open={ask !== null}
        title={
          ask?.kind === 'advance'
            ? `Posunout čas o ${ask.text}?`
            : ask?.kind === 'cancel'
              ? 'Zrušit vybraný závod?'
              : 'Smazat všechna data a vygenerovat demo?'
        }
        confirmLabel={
          ask?.kind === 'advance'
            ? 'Posunout čas'
            : ask?.kind === 'cancel'
              ? 'Zrušit závod'
              : 'Smazat a vygenerovat'
        }
        danger={ask?.kind !== 'advance'}
        {...(ask?.kind === 'reset' ? { requireWord: 'RESET' } : {})}
        onCancel={() => setAsk(null)}
        onConfirm={() => {
          setDone(null);
          if (ask?.kind === 'advance') advance.mutate(ask.body);
          else if (ask?.kind === 'cancel') action.mutate({ action: 'race_cancel', raceId });
          else if (ask?.kind === 'reset') seed.mutate({ reset: true, scale: 'full' });
          setAsk(null);
        }}
      >
        {ask?.kind === 'advance' ? (
          <>
            <p>
              Hodiny hubu se posunou dopředu a engine mezitím vyhodnotí všechny zprávy, které by
              byly na řadě. Zpět se čas vrátit nedá.
            </p>
            <SendTarget me={me} compact />
          </>
        ) : ask?.kind === 'cancel' ? (
          <p>Zrušení závodu zavře všechny jeho cesty a spustí zprávu S05 „Závod zrušen“.</p>
        ) : (
          <p>
            Smažou se všechny závody, kontakty, objednávky, cesty a zprávy a vytvoří se nová demo
            data. Zpět to nejde.
          </p>
        )}
      </ConfirmDialog>
    </>
  );
}
